import React, { useCallback, useMemo, useRef } from "react";
import { Box } from "@material-ui/core";
import RichTextEditor, { documentPreset } from "Components/RichTextEditor";
import "./orderSheet.css";

/**
 * The order sheet editor.
 *
 * The whole sheet is editable, as it was under TinyMCE. TinyMCE additionally
 * locked the fixed parts with `noneditable_class: "mceNonEditable"`; that is
 * not carried over, because editability in ProseMirror is a property of the
 * schema rather than a CSS class and the class itself does not survive a save.
 * The sheet is simply open for editing throughout.
 *
 * This is a thin wrapper over RichTextEditor - it exists to keep the order
 * sheet's styling and its link handling in one place, and to hand the call
 * sites the editor instance in the shape their save/dictation code expects.
 */
export default function OrderSheetEditor({
  value,
  onChange,
  toolbar,
  customItems,
  preset = documentPreset,
  onLinkClick,
  onReady, // (editorsByName) => void
  onActiveEditorChange, // (editor) => void - so dictation can find the caret
  onSave, // (html) => void - Ctrl+S in the sheet
  onPrint, // (html) => void - Ctrl+P in the sheet
  minHeight = 450,
  // The CONTENT scrolls, not the editor and not the dialog around it.
  maxHeight = "60vh",
  // Take the height the container gives instead of a fixed 60vh, so the
  // container never needs a scrollbar of its own. The container must be a
  // flex column with a definite height - see orderSheet.css.
  fillHeight = false,
}) {
  const editorRef = useRef(null);

  // TinyMCE's status bar carried a word count for the sheet; keep it.
  const wordCount = useMemo(() => {
    const text = String(value || "")
      .replace(/<[^>]+>/g, " ")
      .replace(/&nbsp;/g, " ")
      .trim();
    return text ? text.split(/\s+/).length : 0;
  }, [value]);

  const handleReady = useCallback(
    (editor) => {
      editorRef.current = editor;
      if (onActiveEditorChange) {
        editor.on("focus", () => onActiveEditorChange(editor));
        onActiveEditorChange(editor);
      }
      if (onReady) onReady({ document: editor });
    },
    [onReady, onActiveEditorChange]
  );

  return (
    <Box className={fillHeight ? "order-sheet order-sheet--fill" : "order-sheet"}>
      <RichTextEditor
        className="rte--order"
        value={value}
        onChange={onChange}
        preset={preset}
        toolbar={toolbar}
        customItems={customItems}
        onSave={onSave}
        onPrint={onPrint}
        // Filling, the height comes from orderSheet.css; this is only a floor
        // so a very short window still shows a few lines.
        minHeight={fillHeight ? 160 : minHeight}
        maxHeight={fillHeight ? undefined : maxHeight}
        // The "Post this matter" link must not navigate: it would unmount the
        // SPA and take the unsaved draft with it.
        onLinkClick={onLinkClick}
        onReady={handleReady}
      />
      <div className="rte__footer rte__footer--order">
        {wordCount} {wordCount === 1 ? "word" : "words"}
      </div>
    </Box>
  );
}
